import {
  isWorkbookValueListOperator,
  type WorkbookColumnarSheet,
  type WorkbookDiagnostic,
} from "@erp/shared-types";
import {
  parseFormula,
  typeFormula,
  type ColumnType,
  type FormulaResult,
  type FormulaTables,
  type ParsedFormula,
  type TypedFormula,
} from "../formula";
import { lexFormula, type FormulaToken } from "../formulaLexer";
import { reachesItself } from "../graph";
import { RESERVED_PREFIX, isReserved } from "../reservedNames";
import type {
  PlannedColumn,
  PlannedLookup,
  Schema,
  SheetPlan,
} from "../planTypes";

type Lookup = WorkbookColumnarSheet["lookups"][number];
type ColumnToken = Extract<FormulaToken, { kind: "column" }>;

function schemaOf(fields: ReadonlyMap<string, ColumnType>): Schema {
  return [...fields].map(([name, type]) => ({ name, type }));
}
function findIgnoringCase(
  fields: ReadonlyMap<string, ColumnType>,
  name: string,
): string | undefined {
  const lower = name.toLowerCase();
  return [...fields.keys()].find((field) => field.toLowerCase() === lower);
}

class SheetPlanner {
  readonly diagnostics: WorkbookDiagnostic[] = [];
  private readonly parsed: FormulaResult<ParsedFormula>[];

  constructor(
    private readonly sheet: WorkbookColumnarSheet,
    private readonly tables: FormulaTables,
  ) {
    this.parsed = sheet.columns.map((column) =>
      parseFormula(column.expression),
    );
  }

  private report(
    code: WorkbookDiagnostic["code"],
    message: string,
    location: DistributiveOmit<WorkbookDiagnostic["location"], "itemId">,
  ): void {
    this.diagnostics.push({
      code,
      message,
      location: { ...location, itemId: this.sheet.id },
    });
  }

  /** Name rules that hold whatever the input is. */
  checkNames(): void {
    const seen = new Set<string>();
    this.sheet.columns.forEach((column, index) => {
      const lower = column.name.toLowerCase();
      if (seen.has(lower))
        this.report(
          "duplicate-column",
          "Output column names must be unique, ignoring case.",
          { part: "column", index },
        );
      seen.add(lower);
      if (isReserved(column.name))
        this.report(
          "reserved-name",
          `Column names beginning with ${RESERVED_PREFIX} are reserved.`,
          { part: "column", index },
        );
    });
  }

  reportParseProblem(index: number): boolean {
    const result = this.parsed[index];
    if (!result || result.ok) return false;
    this.report(result.problem.code, result.problem.message, {
      part: "column",
      index,
      span: result.problem.span,
    });
    return true;
  }

  reportParseProblems(): void {
    this.sheet.columns.forEach((_, index) => this.reportParseProblem(index));
  }

  applyLookups(
    fields: Map<string, ColumnType>,
    schemas: ReadonlyMap<string, Schema>,
  ): PlannedLookup[] {
    return this.sheet.lookups.flatMap((lookup, index) => {
      const right = new Map(
        (schemas.get(lookup.sourceId) ?? []).map((field) => [
          field.name,
          field.type,
        ]),
      );
      const outputType = this.checkLookup(lookup, index, fields, right);
      if (outputType === null) return [];
      fields.set(lookup.outputColumn, outputType);
      return [{ index, lookup, outputType }];
    });
  }

  /** The Lookup output's type, or null when the output cannot be added. */
  private checkLookup(
    lookup: Lookup,
    index: number,
    fields: ReadonlyMap<string, ColumnType>,
    right: ReadonlyMap<string, ColumnType>,
  ): ColumnType | null {
    const left = fields.get(lookup.inputColumn);
    const match = right.get(lookup.matchColumn);
    const value = right.get(lookup.valueColumn);
    const missing = (
      [
        ["inputColumn", left],
        ["matchColumn", match],
        ["valueColumn", value],
      ] as const
    ).filter(([, type]) => type === undefined);
    for (const [field] of missing)
      this.report(
        "unknown-lookup-column",
        `Unknown column '${lookup[field]}'.`,
        { part: "lookup", index, field },
      );
    if (left && match && left !== match)
      this.report(
        "lookup-type-mismatch",
        "The lookup column and the column it matches must be the same type (for example, both Text). Change one column's type in an earlier worksheet.",
        { part: "lookup", index, field: "matchColumn" },
      );
    if (isReserved(lookup.outputColumn)) {
      this.report(
        "reserved-name",
        `Column names beginning with ${RESERVED_PREFIX} are reserved.`,
        { part: "lookup", index, field: "outputColumn" },
      );
      return null;
    }
    if (findIgnoringCase(fields, lookup.outputColumn) !== undefined) {
      this.report(
        "lookup-output-collision",
        `Lookup output '${lookup.outputColumn}' must have a new column name.`,
        { part: "lookup", index, field: "outputColumn" },
      );
      return null;
    }
    return value ?? "text";
  }

  /**
   * Places Formulas in dependency order, as the engine evaluates them. A Formula
   * reading its own column name reads the input value; any other name waits for
   * that column. The first ready column (in display order) is placed each round.
   */
  planColumns(fields: Map<string, ColumnType>): PlannedColumn[] {
    const joined = new Map(fields);
    const references = this.sheet.columns.map((column) =>
      lexFormula(column.expression).filter(
        (token): token is ColumnToken => token.kind === "column",
      ),
    );
    const pending = this.sheet.columns.map((_, index) => index);
    const pendingNames = (): Set<string> =>
      new Set(pending.map((index) => this.sheet.columns[index]?.name ?? ""));
    const ready = (index: number): boolean => {
      const name = this.sheet.columns[index]?.name;
      const waiting = pendingNames();
      return (references[index] ?? []).every(
        (reference) =>
          fields.has(reference.name) &&
          (reference.name === name || !waiting.has(reference.name)),
      );
    };
    const planned: PlannedColumn[] = [];
    for (;;) {
      const position = pending.findIndex(ready);
      const index = pending[position];
      if (index === undefined) break;
      pending.splice(position, 1);
      const column = this.placeColumn(index, fields, joined);
      if (column) planned.push(column);
    }
    this.reportUnplaced(pending, references, fields);
    return planned;
  }

  private placeColumn(
    index: number,
    fields: Map<string, ColumnType>,
    joined: ReadonlyMap<string, ColumnType>,
  ): PlannedColumn | null {
    const column = this.sheet.columns[index];
    if (!column) return null;
    const existing = findIgnoringCase(joined, column.name);
    if (existing !== undefined && existing !== column.name)
      this.report(
        "column-case-mismatch",
        "Output column names must match the casing of existing input columns or use a distinct name.",
        { part: "column", index },
      );
    const typed = this.typeColumn(index, fields);
    fields.set(column.name, column.type);
    return typed ? { index, column, formula: typed } : null;
  }

  private typeColumn(
    index: number,
    fields: ReadonlyMap<string, ColumnType>,
  ): TypedFormula | null {
    if (this.reportParseProblem(index)) return null;
    const parsed = this.parsed[index];
    if (!parsed?.ok) return null;
    const typed = typeFormula(parsed.formula, fields, this.tables);
    if (typed.ok) return typed.formula;
    this.report(typed.problem.code, typed.problem.message, {
      part: "column",
      index,
      span: typed.problem.span,
    });
    return null;
  }

  /** Columns that never became ready: an unknown reference, or a cycle. */
  private reportUnplaced(
    pending: readonly number[],
    references: readonly (readonly ColumnToken[])[],
    fields: Map<string, ColumnType>,
  ): void {
    const names = new Map(
      pending.map((index) => [this.sheet.columns[index]?.name ?? "", index]),
    );
    const waitsOn = (index: number): number[] =>
      (references[index] ?? []).flatMap((reference) => {
        const other = names.get(reference.name);
        return other === undefined || other === index ? [] : [other];
      });
    for (const index of pending) {
      const column = this.sheet.columns[index];
      if (!column || this.reportParseProblem(index)) continue;
      const unknown = (references[index] ?? []).find(
        (reference) =>
          !fields.has(reference.name) &&
          (reference.name === column.name || !names.has(reference.name)),
      );
      if (unknown)
        this.report("unknown-column", `Unknown column '${unknown.name}'.`, {
          part: "column",
          index,
          span: unknown.span,
        });
      else if (reachesItself(index, waitsOn))
        this.report(
          "column-cycle",
          "These formulas refer to each other (a circular reference). A column can use its own input value, but not a column that uses it.",
          { part: "column", index },
        );
    }
    // Keep filters and sort from repeating the problem as unknown Fields.
    for (const index of pending) {
      const column = this.sheet.columns[index];
      if (column) fields.set(column.name, column.type);
    }
  }

  checkFilterAndSort(fields: ReadonlyMap<string, ColumnType>): void {
    this.sheet.filters.forEach((filter, index) => {
      if (!fields.has(filter.column))
        this.report("unknown-field", `Unknown column '${filter.column}'.`, {
          part: "filter",
          index,
        });
      else if (
        isWorkbookValueListOperator(filter.operator) &&
        !filter.values?.length
      )
        this.report(
          "empty-value-list",
          `The filter on '${filter.column}' lists no values.`,
          { part: "filter", index },
        );
    });
    this.sheet.sort.forEach((sort, index) => {
      if (!fields.has(sort.column))
        this.report("unknown-field", `Unknown column '${sort.column}'.`, {
          part: "sort",
          index,
        });
    });
  }
}

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

/** Plans a columnar Worksheet: its stages once its input and every table it reads have a schema. */
export function planColumnarSheet(
  sheet: WorkbookColumnarSheet,
  schemas: ReadonlyMap<string, Schema>,
  tables: FormulaTables,
  dependencies: readonly string[],
): { plan: SheetPlan; diagnostics: WorkbookDiagnostic[] } {
  const planner = new SheetPlanner(sheet, tables);
  planner.checkNames();
  const output = sheet.columns.map(({ name, type }) => ({ name, type }));
  const input = schemas.get(sheet.inputId);
  if (!input || !dependencies.every((id) => schemas.has(id))) {
    planner.reportParseProblems();
    return {
      plan: { sheetId: sheet.id, output, stages: null },
      diagnostics: planner.diagnostics,
    };
  }
  const fields = new Map(input.map((field) => [field.name, field.type]));
  const lookups = planner.applyLookups(fields, schemas);
  const joined = schemaOf(fields);
  const columns = planner.planColumns(fields);
  planner.checkFilterAndSort(fields);
  return {
    plan: {
      sheetId: sheet.id,
      output,
      stages: { input, lookups, joined, columns, fields: schemaOf(fields) },
    },
    diagnostics: planner.diagnostics,
  };
}
